import { useEffect, useMemo, useState } from 'react';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import { useTranslation } from 'react-i18next';

interface QuestionSuggestionsProps {
  suggestions: Record<string, string[]>;
  onSubmit: (
    answers: Array<{ question: string; answer: string }>,
  ) => void;
}

export function QuestionSuggestions({
  suggestions,
  onSubmit,
}: QuestionSuggestionsProps) {
  const { t } = useTranslation('common');
  const [selectedAnswers, setSelectedAnswers] = useState<
    Record<string, string>
  >({});

  const entries = useMemo(
    () =>
      Object.entries(suggestions).filter(
        ([, options]) => Array.isArray(options) && options.length > 0,
      ),
    [suggestions],
  );

  useEffect(() => {
    setSelectedAnswers({});
  }, [suggestions]);

  if (entries.length === 0) return null;

  const isComplete = entries.every(([question]) => !!selectedAnswers[question]);

  return (
    <div className="flex flex-col gap-3 rounded-lg bg-synergy-neutral-950 p-3">
      <div className="flex items-center justify-between gap-3">
        <p className="text-xs font-medium text-synergy-text-secondary">
          {t('chat.suggestionsHint')}
        </p>
        <Button
          size="sm"
          disabled={!isComplete}
          onClick={() =>
            onSubmit(
              entries.map(([question]) => ({
                question,
                answer: selectedAnswers[question],
              })),
            )
          }
          className="h-7 bg-synergy-blue px-3 text-xs text-white hover:bg-synergy-blue/90"
        >
          {t('chat.suggestionsSubmit')}
        </Button>
      </div>
      {entries.map(([question, options]) => (
        <div key={question} className="flex flex-col gap-1.5">
          <p className="text-xs text-synergy-text-secondary">{question}</p>
          <div className="flex flex-wrap gap-1.5">
          {options.map((option) => (
            <button
              key={option}
              onClick={() =>
                setSelectedAnswers((previous) => ({
                  ...previous,
                  [question]: option,
                }))
              }
              className={cn(
                'rounded-full bg-synergy-neutral-900 px-3 py-1 text-xs text-synergy-text-primary',
                'transition-all duration-150',
                'hover:bg-synergy-blue/10 hover:text-synergy-blue',
                'active:scale-95',
                'disabled:cursor-not-allowed disabled:opacity-50',
                selectedAnswers[question] === option &&
                  'bg-synergy-blue/15 text-synergy-blue',
              )}
            >
              {option}
            </button>
          ))}
          </div>
        </div>
      ))}
    </div>
  );
}
